/**
 * LINKEDIN POST EDIT FORM BODY — Form di modifica inline di un LinkedinPost.
 *
 * Mirror di NoteEditFormBody. Schema: `activityDate` (datetime, EditableField) +
 * `message` (HTML WYSIWYG). Il message segue lo stesso metodo dei Commenti:
 * render sanificato con DOMPurify (anti stored-XSS), edit via RichTextEditor con
 * Salva/Annulla. + picker "Associa ai record" (ActivityTools).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import Button from '@mui/material/Button';
import { EditableField, type EditableValue } from '../../components/EditableField';
import { RichTextEditor } from '../../components/RichTextEditor';
import { useAppDispatch } from '../../store/hooks';
import { useToast } from '../../components/useToast';
import { updateLinkedinPost } from './linkedinPostThunks';
import { getEntityFqn } from '../../entityRegistry/entityRegistry';
import { useAssociatedRecordsBinder } from '../../components/useAssociatedRecordsBinder';
import { ActivityTools } from '../../components/ActivityTools';
import type {
  LinkedinPost,
  LinkedinPostCreatePayload,
  LinkedinPostUpdatePayload,
} from './linkedinPostTypes';

interface LinkedinPostEditFormBodyProps {
  linkedinPost: LinkedinPost;
}

export function LinkedinPostEditFormBody({ linkedinPost }: LinkedinPostEditFormBodyProps) {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { t } = useTranslation();

  const [editingMessage, setEditingMessage] = useState(false);
  const [messageDraft, setMessageDraft] = useState(linkedinPost.message);
  const [savingMessage, setSavingMessage] = useState(false);

  const linkedinPostFqn = getEntityFqn('LinkedinPost');
  const {
    records: currentlyLinked,
    onRecordsChange: handleRecordsChange,
    recordsSaving,
    pickerProps: recordsPickerProps,
  } = useAssociatedRecordsBinder({
    mode: 'edit',
    entityFqn: linkedinPostFqn ?? '',
    entityId: linkedinPost.linkedinPostId,
  });

  const commitField =
    (field: keyof LinkedinPostCreatePayload) =>
    async (newValue: EditableValue) => {
      try {
        const payload = {
          linkedinPostId: linkedinPost.linkedinPostId,
          [field]: newValue,
        } as LinkedinPostUpdatePayload;
        await dispatch(updateLinkedinPost(payload)).unwrap();
        toast({ severity: 'success', message: t('common.saved') });
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        throw err;
      }
    };

  async function saveMessage() {
    setSavingMessage(true);
    try {
      await dispatch(
        updateLinkedinPost({
          linkedinPostId: linkedinPost.linkedinPostId,
          message: messageDraft,
        }),
      ).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
      setEditingMessage(false);
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setSavingMessage(false);
    }
  }

  function cancelMessage() {
    setMessageDraft(linkedinPost.message);
    setEditingMessage(false);
  }

  return (
    <div className="linkedin-post-edit-body">
      <div className="field-row">
        <div className="field-row__label text-xs text-muted">{t('linkedin.activityDate')}</div>
        <EditableField
          value={linkedinPost.activityDate}
          variant={{ kind: 'datetime' }}
          required
          onCommit={commitField('activityDate')}
          label={t('linkedin.activityDate')}
        />
      </div>

      <div className="field-row">
        <div className="field-row__label text-xs text-muted">{t('linkedin.message')}</div>
        {editingMessage ? (
          <div className="linkedin-post-edit-body__message-editor">
            <RichTextEditor
              initialHtml={linkedinPost.message}
              onChange={setMessageDraft}
              placeholder={t('linkedin.messagePlaceholder')}
            />
            <div className="linkedin-post-edit-body__message-actions">
              <Button size="small" variant="contained" onClick={saveMessage} disabled={savingMessage}>
                {t('common.save')}
              </Button>
              <Button size="small" variant="outlined" onClick={cancelMessage} disabled={savingMessage}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <div
            className="message-body linkedin-post-edit-body__message"
            role="button"
            tabIndex={0}
            title={t('editableField.doubleClickToEdit')}
            onDoubleClick={() => setEditingMessage(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setEditingMessage(true);
              }
            }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(linkedinPost.message) }}
          />
        )}
      </div>

      <ActivityTools
        {...recordsPickerProps}
        records={currentlyLinked}
        onChange={(val) => handleRecordsChange?.(val)}
        recordsSaving={recordsSaving}
        entityFqn={linkedinPostFqn ?? ''}
        entityId={linkedinPost.linkedinPostId}
      />
    </div>
  );
}
